import { Card, Masks } from '@tecsinapse/cortex-react'
import React from 'react'
import { useDepartamentosCargosUsuarios } from '@/core/hooks/use-departamentos'
import { OptionSelect } from '@/core/utils/types'
import { baseEnumListToOptions } from '@/core/utils/form'
import { useUsuariosPortalProperties } from '../../hooks/use-portal-properties'
import CargoUsuarioField from './cargo-usuario-field'
import { useUsuarioEdicaoQueryHandler } from '@/modules/usuarios-edicao/hooks/use-query-handler'
import { dealerIsGrupo, syncCargosFromOptions } from '../../utils/utils'
import { useFormContext, useWatch } from 'react-hook-form'
import { CargoUsuarioForm } from '../../types/cargo'
import { FormTextField } from '../../../hook-form-connectors/form-text-field'
import { FormSingleSelect } from '../../../hook-form-connectors/form-single-select'
import { FormMultiSelect } from '../../../hook-form-connectors/form-multi-select'
import { FormDateField } from '../../../hook-form-connectors/form-date-field'
import { FormTextFieldMask } from '../../../hook-form-connectors/form-text-field-mask'
import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import { isDealer } from '@/core/enums/tipo-usuario'
import { getLabel } from '@/core/utils/global-labels'
import { FormCheckboxField } from '../../../hook-form-connectors/form-checkbox-field'
import { useRealmLabels } from '../../../../core/hooks/use-realm-labels'
import { FormPhoneField } from '../../../hook-form-connectors/form-phone-field'
import { useUsuarioEdicaoFirstPageOnboarding } from '../../hooks/use-onboarding-first-page'

interface UsuarioEdicaoFirstPageProps {
  dealerGrupoMap: { [key: string]: string }
  tiposUsuarioMap: [
    {
      id: string
      description: string
    }
  ]
}

export const UsuarioEdicaoFirstPage = ({
  dealerGrupoMap,
  tiposUsuarioMap
}: UsuarioEdicaoFirstPageProps) => {
  const { setValue, getValues } = useFormContext()
  const values = useWatch()
  const realmLabels = useRealmLabels()

  const appendCargo = (novoCargo: CargoUsuarioForm) => {
    setValue('cargos', [...(getValues('cargos') ?? []), novoCargo], {
      shouldDirty: true
    })
  }
  const removeCargo = (index: number) => {
    setValue(
      'cargos',
      (getValues('cargos') ?? []).filter(
        (_: unknown, i: number) => i !== index
      ),
      { shouldDirty: true }
    )
  }

  const {
    idsGrupos,
    id,
    tipo,
    marcas: marcasSelecionadas,
    idsDealers,
    cargos,
    cpf,
    isEstrangeiro
  } = values

  const { isInformarCargo, isInformarGestao, isInformarDepartamentoCargo } =
    useUsuariosPortalProperties()

  const { departamentosOptions } = useDepartamentosCargosUsuarios()

  const { marcas, grupos, dealers, tiposUsuarioDocumentoObrigatorio } =
    useUsuarioEdicaoQueryHandler()
  const tiposUsuarioOptions: OptionSelect[] =
    baseEnumListToOptions(tiposUsuarioMap)
  const marcasOptions: OptionSelect[] = baseEnumListToOptions(marcas)

  const isDocumentoObrigatorio = (tiposUsuarioDocumentoObrigatorio ?? []).some(
    tipo => tipo?.id === values?.tipo?.value
  )

  const filteredGrupos =
    grupos?.filter(grupo =>
      (grupo?.marcas ?? []).some(marca =>
        (marcasSelecionadas || []).some(m => m.value === marca?.id)
      )
    ) ?? []

  const gruposOptions = filteredGrupos.map(grupo => ({
    value: grupo?.id,
    label: grupo?.nome
  }))

  const dealersOptions =
    (dealers &&
      dealers
        .filter(({ grupo }) => dealerIsGrupo(grupo, idsGrupos))
        .map(({ id, nomeExibicao, active }) => ({
          value: id,
          label: active ? nomeExibicao : `${nomeExibicao} - (Inativo)`,
          active
        }))) ||
    []

  const showMarcas = marcasOptions && marcasOptions.length > 1
  const showTipoUsuarios = tiposUsuarioOptions && tiposUsuarioOptions.length > 1
  const disabled = id !== undefined && id !== null
  const showGrupoAndDealers =
    (tipo && tipo.value === 'DEALER_GRUPO') ||
    (tiposUsuarioOptions &&
      tiposUsuarioOptions.length === 1 &&
      tiposUsuarioOptions.some(tipo => tipo.value === 'DEALER_GRUPO'))
  const showLastCard = showMarcas || showGrupoAndDealers
  const showCargos = isInformarCargo(tipo)
  const showGestao = isInformarGestao(tipo)

  const openOnboarding =
    showCargos && (!isDealer(values.tipo.value) || values.idsDealers.length > 0)
  const { setNextStepAndOpen } =
    useUsuarioEdicaoFirstPageOnboarding(openOnboarding)

  return (
    <div>
      <Card className='space-y-deca py-kilo'>
        <p className='font-bold'>
          <Trans>Informações do usuário:</Trans>
        </p>
        <div className='space-y-deca'>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-kilo'>
            <FormTextField type='text' name='nome' label={t`Nome`} />
            <FormTextField
              type='email'
              name='email'
              label={t`E-mail`}
              disabled={disabled}
              customOnBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                setValue('email', e.target.value.toLowerCase())
              }
            />
            {showTipoUsuarios && (
              <FormSingleSelect
                name='tipo'
                label={t`Tipo Usuário`}
                disabled={disabled}
                options={tiposUsuarioOptions}
                customOnChange={() => {
                  setValue('idsDealers', [], { shouldDirty: true })
                  setValue('idsGrupos', [], { shouldDirty: true })
                }}
              />
            )}
          </div>

          <div className='flex flex-row gap-kilo'>
            <div className='flex-1'>
              <FormTextFieldMask
                name='cpf'
                label='CPF'
                mask={Masks.CPF}
                disabled={
                  isEstrangeiro || (!!id && !!cpf && isDocumentoObrigatorio)
                }
              />
            </div>
            <div className='flex-1'>
              <FormPhoneField
                name='telefones'
                labelInput={t`Telefone`}
                labelTags={t`Telefones incluídos`}
              />
            </div>
            <div className='flex-1'>
              <FormPhoneField
                name='celulares'
                labelInput={t`Celular`}
                labelTags={t`Celulares incluídos`}
              />
            </div>
          </div>
          {!realmLabels.has('somente.brasileiros') ? (
            <FormCheckboxField
              name='isEstrangeiro'
              label={t`Usuário Estrangeiro`}
              disabled={values.id}
            />
          ) : null}
        </div>
      </Card>

      {showLastCard ? (
        <Card className='space-y-deca mt-deca'>
          <p className='font-bold'>
            <Trans>Informações de {getLabel('label.dealer')}:</Trans>
          </p>
          {showMarcas && (
            <FormMultiSelect
              name='marcas'
              label={getLabel('label.marcas')}
              options={marcasOptions}
              customOnChange={() => {
                setValue('idsDealers', [], { shouldDirty: true })
                setValue('idsGrupos', [], { shouldDirty: true })
              }}
            />
          )}

          {showGrupoAndDealers && (
            <>
              <FormMultiSelect
                name='idsGrupos'
                label={getLabel('label.grupos')}
                withSearch
                options={gruposOptions}
                customOnChange={(idsGruposSelecionados: unknown[]) => {
                  const newDealers = idsDealers.filter(
                    id =>
                      idsGruposSelecionados.indexOf(dealerGrupoMap[id.value]) >
                      -1
                  )
                  setValue('idsDealers', newDealers, { shouldDirty: true })
                }}
                disabled={gruposOptions.length === 0}
              />
              <FormMultiSelect
                name='idsDealers'
                label={getLabel('label.dealers')}
                options={dealersOptions}
                customOnChange={(v: OptionSelect[]) => {
                  setValue('cargos', syncCargosFromOptions(v, cargos), {
                    shouldDirty: true
                  })
                }}
                disabled={values.idsGrupos.length === 0}
              />
            </>
          )}
        </Card>
      ) : null}

      {showGestao ? (
        <Card className='space-y-deca mt-deca'>
          <p className='font-bold'>
            <Trans>Informações de gestão:</Trans>
          </p>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-kilo'>
            <FormTextField
              name='emailSuperior'
              type='email'
              label={t`Email do superior`}
              customOnBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                setValue('emailSuperior', e.target.value.toLowerCase())
              }
            />
            <FormDateField name='dataAdmissao' label={t`Data de admissão`} />
          </div>
        </Card>
      ) : null}

      {showCargos ? (
        <>
          {!isDealer(values.tipo.value) || values.idsDealers.length > 0 ? (
            <Card className='space-y-deca mt-deca'>
              <p className='font-bold'>
                <Trans>Informações de cargo:</Trans>
              </p>
              <CargoUsuarioField
                append={appendCargo}
                remove={removeCargo}
                name={'cargos'}
                showDepartamentos={isInformarDepartamentoCargo}
                departamentoOptions={departamentosOptions}
                setOpenTour={setNextStepAndOpen}
                tourId={'first-cargos'}
              />
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
